"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import PostCard from "./PostCard";

// Fetch posts from the API
async function getPosts(page = 1) {
  const res = await fetch(`/api/posts?page=${page}`);
  if (!res.ok) {
    throw new Error("Failed to fetch posts");
  }
  return res.json();
}

// Skeleton loader component
const SkeletonLoader = () => (
  <div className="rounded-lg min-h-full shadow animate-pulse">
    <div className="relative h-48 rounded-t-lg overflow-hidden bg-blue-10"></div>
    <div className="p-4">
      <div className="flex items-end space-x-2 mb-2">
        <div className="h-4 bg-blue-10 rounded w-1/4"></div>
        <div className="h-4 bg-blue-10 rounded w-1/4"></div>
      </div>
      <div className="h-6 bg-blue-10 rounded w-3/4 mb-2"></div>
      <div className="h-4 bg-blue-10 rounded w-full mb-2"></div>
      <div className="h-4 bg-blue-10 rounded w-2/3"></div>
    </div>
  </div>
);

export default function PostList() {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const loaderRef = useRef(null);

  const loadMorePosts = useCallback(async () => {
    setLoading((prevLoading) => {
      if (prevLoading || !hasMore) return prevLoading;
      return true;
    });
    try {
      const { posts: newPosts, hasMore: moreAvailable } = await getPosts(page);
      setPosts((prevPosts) => [...prevPosts, ...newPosts]);
      setHasMore(moreAvailable);
      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      console.error("Error loading posts:", error);
      setError("Failed to load more posts. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [hasMore, page]);

  useEffect(() => {
    const fetchInitialPosts = async () => {
      try {
        const { posts: initialPosts, hasMore: moreAvailable } = await getPosts(1);
        setPosts(initialPosts);
        setHasMore(moreAvailable);
        setPage(2);
      } catch (error) {
        console.error("Error loading initial posts:", error);
        setError("Failed to load posts. Please try again later.");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialPosts();
  }, []);

  useEffect(() => {
    if (initialLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMorePosts();
        }
      },
      { threshold: 1.0 }
    );
    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }
    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMore, loading, loadMorePosts, initialLoading]);

  return (
    <div className="max-w-7xl mx-auto py-10 container">
      {error && <div className="text-center text-red-500">{error}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {initialLoading
          ? Array.from({ length: 6 }).map((_, index) => (
              <SkeletonLoader key={`initial-${index}`} />
            ))
          : posts.map((post) => <PostCard key={post.post_id} post={post} />)}
        
        {/* Show skeleton loaders while loading more posts */}
        {loading && !initialLoading && 
          Array.from({ length: 3 }).map((_, index) => (
            <SkeletonLoader key={`loading-${index}`} />
          ))
        }
      </div>
      {!initialLoading && hasMore && (
        <div ref={loaderRef} className="text-center py-4">
          {!loading && "Scroll for more"}
        </div>
      )}
    </div>
  );
}